import React from 'react';
import { Grid, ToggleButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AgendaStatusFilter } from '../utils/occupancyMath';
import { HIDDEN_SCROLLBAR_SX } from '../agendaStyles';

type StatusFilterButtonsProps = {
  counts: Record<AgendaStatusFilter, number>;
  value: AgendaStatusFilter;
  onChange: (filter: AgendaStatusFilter) => void;
  leading?: React.ReactNode;
};

const FILTER_ORDER = [
  AgendaStatusFilter.ALL,
  AgendaStatusFilter.NOT_ARRIVED,
  AgendaStatusFilter.HERE,
  AgendaStatusFilter.OPEN_BILL,
  AgendaStatusFilter.NO_TABLE,
];

/**
 * One tab per card-state filter, with the number of orders each would show. Each is a standalone toggle button:
 * the theme styles a selected one as the filter tab.
 */
export const StatusFilterButtons = ({ counts, value, onChange, leading }: StatusFilterButtonsProps) => {
  const { t } = useTranslation();
  return (
    <Grid
      container
      alignItems="center"
      gap={1}
      sx={{
        overflowX: { xs: 'auto', md: 'visible' },
        flexWrap: { xs: 'nowrap', md: 'wrap' },
        flexShrink: 0,
        // Room for the focus ring, which the phone's horizontal scroll would otherwise clip.
        paddingBlock: { xs: '4px', md: 0 },
        marginBlock: { xs: '-4px', md: 0 },
        paddingInline: { xs: '4px', md: 0 },
        marginInline: { xs: '-4px', md: 0 },
        // The negative margins only work when the row is 8px wider than its parent (Grid containers are width 100%).
        width: { xs: 'calc(100% + 8px)', md: '100%' },
        ...HIDDEN_SCROLLBAR_SX,
      }}>
      {leading}
      {FILTER_ORDER.filter((filter) => filter !== AgendaStatusFilter.NO_TABLE || counts[filter] > 0).map((filter) => {
        const isSelected = value === filter;
        return (
          <ToggleButton
            key={filter}
            value={filter}
            selected={isSelected}
            aria-pressed={isSelected}
            onChange={() => onChange(filter)}
            sx={{ minHeight: { xs: 40, md: 44 }, fontSize: 15, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {t(`calendar.areas_view.filter_${filter}`)}
            <span style={{ opacity: 0.75, marginInlineStart: 6 }}>{counts[filter]}</span>
          </ToggleButton>
        );
      })}
    </Grid>
  );
};
